import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { NotificationService } from '@/app/core/services/common/notification.service';
import { AuthService } from '../../pages/auth/services/auth.service';
import { I18nService } from '../../core/services/common/i18n.service';
import { Menu } from '@/app/pages/menu-system/models/menu.model';
import { Store } from '@ngrx/store';
import * as AuthSelectors from '@app/store/auth/auth.selectors';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly notification = inject(NotificationService);
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);
  private readonly staticRoutes = ['/', '/dashboard', '/home'];
  menus$ = this.store.select(AuthSelectors.selectMenus);

  //TODO: Need check with permissions or roles
  private getMenuRoutes(userRoles: string[] = []): Observable<string[]> {
    return this.menus$.pipe(
      map((menuItems) => {
        return this.extractRoutesFromMenuItems(menuItems || []);
      }),
      catchError((error) => {
        console.error('AuthGuard - Error processing menus:', error);
        return of([]);
      }),
    );
  }

  private extractRoutesFromMenuItems(menuItems: Menu[]): string[] {
    const extractRoutes = (items: Menu[]): string[] =>
      items.flatMap((item) => [
        ...(item.C_URL ? [item.C_URL] : []),
        ...(item.LIST_CHILD ? extractRoutes(item.LIST_CHILD) : []),
      ]);

    return extractRoutes(menuItems);
  }

  private isRouteAllowed(route: string, allowedRoutes: string[]): boolean {
    return (
      route === '/' || allowedRoutes.some((allowed) => allowed !== '/' && route.startsWith(allowed))
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | boolean {
    if (state.url.startsWith('/unauthorized') || state.url.startsWith('/login') || state.url.startsWith('/kios')) {
      return true;
    }

    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login']);
      return false;
    }

    if (this.authService.isAdmin()) {
      return true;
    }

    if (this.staticRoutes.includes(state.url)) {
      return true;
    }

    //TODO: Cần xác định permissions hoặc roles để kiểm tra quyền truy cập
    const userRoles = [];

    return this.getMenuRoutes([]).pipe(
      map((allowedRoutes) => {
        if (this.isRouteAllowed(state.url, allowedRoutes)) {
          return true;
        }
        console.warn('Access denied to route:', state.url);
        this.router.navigate(['/unauthorized'], { queryParams: { returnUrl: state.url } });
        this.notification.error(this.i18n.translate('auth.errors.accessDenied'));
        return false;
      }),
      catchError(() => {
        this.router.navigate(['/unauthorized']);
        this.notification.error(this.i18n.translate('auth.errors.permissionCheckFailed'));
        return of(false);
      }),
    );
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | boolean {
    // Allow kios routes without authentication
    if (state.url.startsWith('/kios')) {
      return true;
    }
    return this.canActivate(route, state);
  }
}

const createGuardFn = (
  checkFn: (authService: AuthService, data: string[]) => boolean,
  dataKey: string,
  errorKey: string,
) => {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const notification = inject(NotificationService);
    const i18n = inject(I18nService);

    const data = route.data[dataKey] as string[] | undefined;

    if (!data?.length) return true;

    if (checkFn(authService, data)) return true;

    notification.error(i18n.translate(`auth.errors.${errorKey}`));
    return router.createUrlTree(['/unauthorized'], { queryParams: { returnUrl: state.url } });
  };
};
